import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LogOut, Mail, Calendar as CalendarIcon, User, Loader2, Image as ImageIcon, Users, BarChart3, Eye, MousePointer, ExternalLink, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import MediaManagement from '../components/MediaManagement';
import ContentManager from '../components/ContentManager';
import AvailabilityCalendar from '../components/AvailabilityCalendar';

const API = '/api';

// Statistics Component
const StatisticsPanel = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [username, password] = token.split(':');
        const response = await axios.get(`${API}/stats`, {
          auth: { username, password }
        });
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (token) fetchStats();
  }, [token]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!stats) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <BarChart3 className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No statistics available yet</p>
        </CardContent>
      </Card>
    );
  }

  const { totals, daily, top_images } = stats;

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h2 className="font-playfair text-2xl font-semibold mb-2">Site Statistics</h2>
        <p className="text-muted-foreground">Privacy-focused analytics (no cookies)</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-summary">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-blue-100">
                <Eye className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Visits</p>
                <p className="text-2xl font-bold" data-testid="stat-visits">{totals.total_visits || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-green-100">
                <MousePointer className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Image Clicks</p>
                <p className="text-2xl font-bold" data-testid="stat-image-clicks">{totals.total_image_clicks || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-orange-100">
                <ExternalLink className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Airbnb Clicks</p>
                <p className="text-2xl font-bold" data-testid="stat-airbnb-clicks">{totals.total_booking_airbnb || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-purple-100">
                <Mail className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Direct Inquiries</p>
                <p className="text-2xl font-bold" data-testid="stat-direct-clicks">{totals.total_booking_direct || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Daily Stats */}
      {daily && daily.length > 0 && (
        <Card>
          <CardHeader>
            <h3 className="font-semibold">Last 7 Days</h3>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-4">Date</th>
                    <th className="text-right py-2 px-4">Visits</th>
                    <th className="text-right py-2 px-4">Image Clicks</th>
                    <th className="text-right py-2 px-4">Bookings</th>
                  </tr>
                </thead>
                <tbody>
                  {daily.map((day) => (
                    <tr key={day.date} className="border-b">
                      <td className="py-2 px-4">{day.date}</td>
                      <td className="text-right py-2 px-4">{day.events?.visit || 0}</td>
                      <td className="text-right py-2 px-4">{day.events?.image_click || 0}</td>
                      <td className="text-right py-2 px-4">
                        {(day.events?.booking_click_airbnb || 0) + (day.events?.booking_click_direct || 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Images */}
      {top_images && top_images.length > 0 && (
        <Card>
          <CardHeader>
            <h3 className="font-semibold">Most Clicked Images</h3>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {top_images.map((img, idx) => (
                <div key={img.id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="text-muted-foreground w-6">{idx + 1}.</span>
                    <span className="font-medium">{img.title || 'Untitled'}</span>
                  </div>
                  <span className="text-sm text-muted-foreground">{img.click_count} clicks</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      setIsLoggedIn(true);
      fetchLeads(token);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/auth/admin-login`, credentials);

      if (response.data.success) {
        localStorage.setItem('admin_token', response.data.token);
        setIsLoggedIn(true);
        toast.success('Login successful!');
        fetchLeads(response.data.token);
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLeads = async (token) => {
    try {
      const [username, password] = token.split(':');
      const response = await axios.get(`${API}/leads`, {
        auth: { username, password }
      });
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsLoggedIn(false);
    setLeads([]);
    toast.success('Logged out successfully');
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted stone-texture" data-testid="admin-login-page">
        <Card className="w-full max-w-md mx-4" data-testid="login-card">
          <CardHeader>
            <h1 className="font-playfair text-3xl font-bold text-center" data-testid="login-title">
              Admin Login
            </h1>
            <p className="text-center text-muted-foreground mt-2" data-testid="login-subtitle">
              Villa Renaissance Garden
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div data-testid="login-username-field">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  type="text"
                  value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  required
                  className="rounded-sm"
                  data-testid="login-username-input"
                />
              </div>
              <div data-testid="login-password-field">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  required
                  className="rounded-sm"
                  data-testid="login-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-primary hover:bg-primary/90 rounded-sm"
                data-testid="login-submit-button"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Logging in...
                  </>
                ) : (
                  'Login'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Dashboard Screen
  return (
    <div className="min-h-screen bg-background stone-texture pt-20" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex justify-between items-center mb-12">
          <div>
            <h1 className="font-playfair text-4xl font-bold" data-testid="dashboard-title">Admin Dashboard</h1>
            <p className="text-muted-foreground mt-2" data-testid="dashboard-subtitle">
              Manage your villa website
            </p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="rounded-sm"
            data-testid="logout-button"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </Button>
        </div>

        <Tabs defaultValue="leads" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 max-w-3xl">
            <TabsTrigger value="leads" className="flex items-center gap-2">
              <Users size={16} />
              Inquiries
            </TabsTrigger>
            <TabsTrigger value="calendar" className="flex items-center gap-2">
              <CalendarIcon size={16} />
              Calendar
            </TabsTrigger>
            <TabsTrigger value="content" className="flex items-center gap-2">
              <FileText size={16} />
              Content
            </TabsTrigger>
            <TabsTrigger value="media" className="flex items-center gap-2">
              <ImageIcon size={16} />
              Gallery
            </TabsTrigger>
            <TabsTrigger value="stats" className="flex items-center gap-2">
              <BarChart3 size={16} />
              Statistics
            </TabsTrigger>
          </TabsList>

          <TabsContent value="leads" className="space-y-6">
            <div className="mb-6">
              <h2 className="font-playfair text-2xl font-semibold mb-2">Guest Inquiries</h2>
              <p className="text-muted-foreground">
                {leads.length} {leads.length === 1 ? 'inquiry' : 'inquiries'} received
              </p>
            </div>

            {leads.length === 0 ? (
              <Card data-testid="no-leads-message">
                <CardContent className="p-12 text-center">
                  <Mail className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No inquiries yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6" data-testid="leads-list">
                {leads.map((lead, index) => (
                  <Card key={lead.id} className="border-border hover:shadow-lg transition-shadow" data-testid={`lead-card-${index}`}>
                    <CardContent className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <div className="flex items-center gap-2 mb-4">
                            <User className="text-primary" size={20} />
                            <h3 className="font-semibold text-xl" data-testid={`lead-name-${index}`}>{lead.name}</h3>
                            {lead.status === 'new' && (
                              <span className="bg-secondary text-white text-xs px-2 py-1 rounded-sm" data-testid={`lead-status-${index}`}>
                                NEW
                              </span>
                            )}
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2" data-testid={`lead-email-${index}`}>
                              <Mail className="text-muted-foreground" size={16} />
                              <a href={`mailto:${lead.email}`} className="text-primary hover:underline">
                                {lead.email}
                              </a>
                            </div>
                            <div className="flex items-center gap-2" data-testid={`lead-dates-${index}`}>
                              <CalendarIcon className="text-muted-foreground" size={16} />
                              <span>
                                {lead.check_in} to {lead.check_out}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground mb-2">Message:</p>
                          <p className="text-sm" data-testid={`lead-message-${index}`}>{lead.message}</p>
                          <p className="text-xs text-muted-foreground mt-4" data-testid={`lead-date-${index}`}>
                            Received: {new Date(lead.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="calendar" className="space-y-6">
            <div className="mb-6">
              <h2 className="font-playfair text-2xl font-semibold mb-2">Availability Calendar</h2>
              <p className="text-muted-foreground">
                View and manage property availability synced from Airbnb iCal
              </p>
            </div>
            <div className="max-w-lg mx-auto">
              <AvailabilityCalendar 
                showRefresh={true} 
                token={localStorage.getItem('admin_token')} 
              />
            </div>
            <Card className="mt-6">
              <CardContent className="p-6">
                <h3 className="font-semibold mb-2">About iCal Sync</h3>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li>• Click <strong>"Airbnb iCal"</strong> or <strong>"Vrbo iCal"</strong> to immediately pull the latest availability from each platform</li>
                  <li>• Both calendars are merged to show all blocked dates from both platforms</li>
                  <li>• Blocked dates show the nights a guest is staying (check-in to the night before check-out)</li>
                  <li>• The check-out day is automatically made available for the next guest</li>
                  <li>• Calendar data is cached in the database for faster loading</li>
                </ul>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="content">
            <ContentManager token={localStorage.getItem('admin_token')} />
          </TabsContent>

          <TabsContent value="media">
            <MediaManagement token={localStorage.getItem('admin_token')} />
          </TabsContent>

          <TabsContent value="stats">
            <StatisticsPanel token={localStorage.getItem('admin_token')} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
